/**
 * 招待管理用のAPI hooks
 */
import { useCallback, useContext, useEffect, useState } from "react";
import { RestApiError, restClient } from "../lib/rest-client";
import { AuthContext } from "./authProvider/authProvider";

/**
 * 招待情報の型
 */
export interface Invitation {
  id: string;
  tenantId: string;
  tenantName: string;
  email: string;
  role: string;
  crmRole: string | null;
  status: string;
  expiresAt: string;
  invitationUrl: string;
  emailSent: boolean;
}

/**
 * 招待作成リクエストの型
 */
export interface CreateInvitationInput {
  email: string;
  role: "admin" | "member";
  crmRole?: string | null;
  sendEmail?: boolean;
}

/**
 * 招待一覧取得hookの結果
 */
export interface UseInvitationsResult {
  invitations: Invitation[];
  loading: boolean;
  error: RestApiError | null;
  refetch: () => Promise<void>;
}

/**
 * 招待作成hookの結果
 */
export interface UseCreateInvitationResult {
  createInvitation: (input: CreateInvitationInput) => Promise<Invitation>;
  loading: boolean;
  error: RestApiError | null;
}

/**
 * 招待キャンセルhookの結果
 */
export interface UseCancelInvitationResult {
  cancelInvitation: (invitationId: string) => Promise<void>;
  loading: boolean;
  error: RestApiError | null;
}

/**
 * テナントIDを取得するヘルパー
 * @return {string | null} テナントID
 */
function useTenantId(): string | null {
  const { currentUser } = useContext(AuthContext);
  const tenants = currentUser?.tenants;
  if (!tenants || tenants.length === 0) return null;
  return tenants[0].id;
}

/**
 * 招待一覧を取得するhook
 * @return {UseInvitationsResult} 招待一覧、ローディング状態、エラー
 */
export function useInvitations(): UseInvitationsResult {
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<RestApiError | null>(null);
  const tenantId = useTenantId();

  const fetchInvitations = useCallback(async () => {
    if (!tenantId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await restClient.get<Invitation[]>(
        `/tenants/${tenantId}/invitations`,
      );
      setInvitations(response);
    } catch (e) {
      if (e instanceof RestApiError) {
        setError(e);
      } else {
        setError(
          new RestApiError(
            e instanceof Error ? e.message : "Unknown error",
            500,
          ),
        );
      }
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchInvitations();
  }, [fetchInvitations]);

  return { invitations, loading, error, refetch: fetchInvitations };
}

/**
 * 招待を作成するhook
 * @return {UseCreateInvitationResult} 招待作成関数、ローディング状態、エラー
 */
export function useCreateInvitation(): UseCreateInvitationResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RestApiError | null>(null);
  const tenantId = useTenantId();

  const createInvitation = useCallback(
    async (input: CreateInvitationInput): Promise<Invitation> => {
      if (!tenantId) {
        throw new RestApiError("テナントが選択されていません", 400);
      }

      setLoading(true);
      setError(null);

      try {
        const response = await restClient.post<Invitation>(
          `/tenants/${tenantId}/invitations`,
          input,
        );
        return response;
      } catch (e) {
        const apiError =
          e instanceof RestApiError
            ? e
            : new RestApiError(
                e instanceof Error ? e.message : "Unknown error",
                500,
              );
        setError(apiError);
        throw apiError;
      } finally {
        setLoading(false);
      }
    },
    [tenantId],
  );

  return { createInvitation, loading, error };
}

/**
 * 招待をキャンセルするhook
 * @return {UseCancelInvitationResult} キャンセル関数、ローディング状態、エラー
 */
export function useCancelInvitation(): UseCancelInvitationResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RestApiError | null>(null);
  const tenantId = useTenantId();

  const cancelInvitation = useCallback(
    async (invitationId: string): Promise<void> => {
      if (!tenantId) {
        throw new RestApiError("テナントが選択されていません", 400);
      }

      setLoading(true);
      setError(null);

      try {
        await restClient.delete(
          `/tenants/${tenantId}/invitations/${invitationId}`,
        );
      } catch (e) {
        const apiError =
          e instanceof RestApiError
            ? e
            : new RestApiError(
                e instanceof Error ? e.message : "Unknown error",
                500,
              );
        setError(apiError);
        throw apiError;
      } finally {
        setLoading(false);
      }
    },
    [tenantId],
  );

  return { cancelInvitation, loading, error };
}
